// =============================================================
// Review Center (Phase 9.3) — surfaces the server-side Review Center built in
// Phase 6. Lists parked offline mutations from GET /sync/review-items (deleted /
// reassigned targets) with the original payload + base values for context, and
// resolves each via POST /sync/review-items/:id/resolve:
//   - APPLY   → force-applies the parked mutation (operator override)
//   - DISCARD → drops it (the delete/reassign was intentional)
//
// RBAC: resolving needs tasks:Full (the server enforces tasks:delete on resolve);
// the action buttons are disabled without it. The notification bell surfaces a
// "parked sync change" item per open row that deep-links here.
// =============================================================

import { useState } from "react";
import { ApiError, type ReviewItem } from "@perpetuum/core";
import { useResolveReviewItem, useReviewItems } from "../lib/queries.js";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, Icon, ICONS } from "../components/primitives.js";
import { Skeleton } from "../components/Skeleton.js";

const REASON_KEY: Record<ReviewItem["reason"], string> = {
  target_deleted: "review.reason.target_deleted",
  target_reassigned: "review.reason.target_reassigned",
};

function preview(obj?: Record<string, unknown> | null): string {
  if (!obj || Object.keys(obj).length === 0) return "—";
  return Object.entries(obj)
    .slice(0, 4)
    .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
    .join(", ");
}

export function Review() {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast, confirm } = useChrome();
  const [tab, setTab] = useState<"open" | "resolved">("open");
  const itemsQ = useReviewItems(tab);
  const resolve = useResolveReviewItem();
  const canResolve = can("tasks", "delete");

  const items = itemsQ.data ?? [];

  const onResolve = async (item: ReviewItem, resolution: "apply" | "discard") => {
    const ok = await confirm({
      title: resolution === "apply" ? t("review.applyTitle") : t("review.discardTitle"),
      body:
        resolution === "apply"
          ? t("review.applyBody", { op: item.op, table: item.targetTable })
          : t("review.discardBody", { op: item.op, table: item.targetTable }),
      danger: resolution === "discard",
      confirmLabel: resolution === "apply" ? t("review.applyConfirm") : t("review.discardConfirm"),
    });
    if (!ok) return;
    try {
      await resolve.mutateAsync({ id: item.id, resolution });
      toast(resolution === "apply" ? t("review.applied") : t("review.discarded"), "success");
    } catch (e) {
      const code = e instanceof ApiError ? (e.code ?? `error_${e.status}`) : "network_error";
      toast(code === "forbidden" ? t("review.notPermitted") : t("review.resolveErr", { code }), "error");
    }
  };

  return (
    <div>
      <h2 className="d-h2">{t("review.title")}</h2>
      <p className="d-sub">{t("review.sub")}</p>

      <div className="wb-filters">
        {(["open", "resolved"] as const).map((k) => (
          <button key={k} className={`wb-fchip ${tab === k ? "on" : ""}`} onClick={() => setTab(k)} data-testid={`review-tab-${k}`}>
            {k === "open" ? t("review.tab.open") : t("review.tab.resolved")}
          </button>
        ))}
      </div>

      <div className="d-card">
        <div className="d-card-body" style={{ padding: 0 }}>
          {itemsQ.isLoading && (
            <div style={{ padding: 18 }}>
              <Skeleton variant="text" count={3} />
            </div>
          )}
          {!itemsQ.isLoading && items.length === 0 && (
            <div className="wb-empty" data-testid="review-empty">
              {tab === "open" ? t("review.emptyOpen") : t("review.emptyResolved")}
            </div>
          )}
          {items.map((item) => (
            <div className="review-row" key={item.id} data-testid={`review-row-${item.id}`}>
              <div className="review-main">
                <div className="review-top">
                  <span className={`prio-chip ${item.reason === "target_deleted" ? "critical" : "warning"}`}>
                    {t(REASON_KEY[item.reason])}
                  </span>
                  <span className="review-op">{item.op}</span>
                  <span className="review-table">{item.targetTable}</span>
                  <span className="review-id">#{item.targetId.slice(0, 8)}</span>
                </div>
                <div className="review-kv">
                  <span className="review-k">{t("review.colChange")}</span>
                  <span className="review-v">{preview(item.payload)}</span>
                </div>
                {item.baseValues && (
                  <div className="review-kv">
                    <span className="review-k">{t("review.colWas")}</span>
                    <span className="review-v">{preview(item.baseValues)}</span>
                  </div>
                )}
              </div>
              {tab === "open" ? (
                <div className="review-actions">
                  <Btn
                    variant="go"
                    size="sm"
                    disabled={!canResolve || resolve.isPending}
                    onClick={() => void onResolve(item, "apply")}
                    data-testid={`review-apply-${item.id}`}
                  >
                    <Icon d={ICONS.check} size={14} />
                    {t("review.apply")}
                  </Btn>
                  <Btn
                    variant="stop"
                    size="sm"
                    disabled={!canResolve || resolve.isPending}
                    onClick={() => void onResolve(item, "discard")}
                    data-testid={`review-discard-${item.id}`}
                  >
                    <Icon d={ICONS.trash} size={14} />
                    {t("review.discard")}
                  </Btn>
                </div>
              ) : (
                <div className="review-actions">
                  <span className="ro-badge">{item.resolution ?? t("review.resolved")}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
